import type { Person, Relationship } from './types'

/**
 * Fan chart layout (v0.24.0, D-143) — the ancestor pedigree as concentric rings.
 *
 * The eight lenses already answer "who should I see". This answers a different
 * question: how do five generations fit on a phone held in one hand? The layered
 * constellation grows sideways — each generation up doubles the width, so by the
 * great-grandparents you are panning a canvas several screens wide. A fan spends
 * that same growth on ANGLE instead of width, so depth costs almost nothing: ring
 * five sits a few hundred pixels from ring one no matter how full it is.
 *
 * Numbering is Ahnentafel, the genealogical convention since 1590: the root is 1,
 * a person's father is 2n and their mother 2n+1. That single rule does all the
 * geometry for free — the two parents of the slot at index i in a ring land at
 * 2i and 2i+1 in the next ring out, which is exactly the two halves of the child's
 * angular span. No layout search, no collision pass; the arithmetic IS the layout.
 *
 * Empty slots are deliberate and bounded. A pedigree has 2^n slots per ring and
 * real families have holes, so rendering every hole would draw 31 grey wedges
 * around a family that has recorded four people. Instead a gap is drawn only when
 * its CHILD is known — that turns each hole into the specific, answerable question
 * "who was her mother?" rather than a wall of absence.
 *
 * The default sweep is 270°, and the reasoning is worth recording because the
 * obvious answers are both wrong. A half-fan is the printed convention, but paper
 * is landscape and a phone is not — it wastes a third of the height. A full circle
 * looks like the compact choice and is not: every sweep has a bounding box exactly
 * 2R wide, so on a 390px phone R is pinned at ~183 no matter what, which means more
 * sweep is more room at no cost in radius right up until the labels at the bottom
 * of a full circle read upside down. 270° takes the extra room and stops before
 * that, and the quadrant it leaves open is useful furniture rather than waste.
 *
 * Pure function, no React, same shape as layoutTree in graph.ts.
 */

export const FAN_CORE_R = 52      // radius of the centre disc (the root person)
export const FAN_RING_W = 76      // thickness of each generation ring
export const FAN_MAX_DEPTH = 6    // beyond this a slot is thinner than a fingertip

/**
 * Which SLOT of the couple, not which line of the family. The father's own mother
 * sits in a 'mother' slot while being squarely on the paternal line — the classic
 * fan colours by slot, and which line someone belongs to is already told by which
 * half of the circle they are in.
 */
export type FanSide = 'root' | 'father' | 'mother'

export interface FanSlot {
  ahnen: number            // Ahnentafel number; 1 = root, father = 2n, mother = 2n+1
  gen: number              // 0 = root
  person: Person | null    // null = a known gap, an invitation to fill it
  childAhnen: number | null  // whose parent this slot is (null for the root)
  childPerson: Person | null // ...and that child, so a gap can say "add Ali's mother"
  side: FanSide
  a0: number; a1: number   // radians, screen space (0 = east, increasing clockwise)
  r0: number; r1: number
  path: string             // annular sector; '' for the root disc
  labelX: number
  labelY: number
  labelRotate: number      // degrees, already flipped so text is never upside down
  spanDeg: number          // how much room this slot has — the caller decides what fits
}

export interface FanLayout {
  slots: FanSlot[]
  width: number            // viewBox, tight to the arc actually drawn
  height: number
  cx: number
  cy: number
  depth: number            // rings actually drawn
  known: number            // ancestors found
  gaps: number             // fillable holes
}

export interface FanOpts {
  depth?: number           // generations above the root (default 4)
  sweep?: number           // degrees of arc (default 270; 360 and 180 both work)
}

const TAU = Math.PI * 2
const rad = (d: number) => (d * Math.PI) / 180

const pt = (cx: number, cy: number, a: number, r: number) =>
  `${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`

/** SVG path for one annular sector (a ring segment between two angles). */
function sectorPath(cx: number, cy: number, a0: number, a1: number, r0: number, r1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0
  return [
    `M ${pt(cx, cy, a0, r1)}`,
    `A ${r1} ${r1} 0 ${large} 1 ${pt(cx, cy, a1, r1)}`,
    `L ${pt(cx, cy, a1, r0)}`,
    `A ${r0} ${r0} 0 ${large} 0 ${pt(cx, cy, a0, r0)}`,
    'Z',
  ].join(' ')
}

/**
 * Which parent is the father's slot? Gender when we have it, otherwise stable
 * insertion order — a pedigree with an unrecorded gender should still draw, and
 * guessing from a name would be worse than an arbitrary but consistent choice.
 */
function orderParents(parents: Person[]): [Person | null, Person | null] {
  const male = parents.find(p => p.gender === 'male') || null
  const female = parents.find(p => p.gender === 'female') || null
  const rest = parents.filter(p => p !== male && p !== female)
  const father = male ?? rest.shift() ?? null
  const mother = female ?? rest.shift() ?? null
  return [father, mother]
}

export function layoutFan(
  persons: Person[],
  relationships: Relationship[],
  rootId: string | undefined,
  opts: FanOpts = {}
): FanLayout {
  const maxDepth = Math.max(1, Math.min(FAN_MAX_DEPTH, opts.depth ?? 4))
  const sweepDeg = Math.max(90, Math.min(360, opts.sweep ?? 270))

  const byId = new Map(persons.map(p => [p.id, p]))
  const root = rootId ? byId.get(rootId) : undefined
  if (!root) {
    const r = (FAN_CORE_R + FAN_RING_W + 16) * 2
    return { slots: [], width: r, height: r, cx: r / 2, cy: r / 2, depth: 0, known: 0, gaps: 0 }
  }

  const parentsOf = new Map<string, Person[]>()
  for (const r of relationships) {
    if (r.type !== 'parent') continue
    const parent = byId.get(r.person1_id)
    if (!parent || !byId.has(r.person2_id)) continue
    parentsOf.set(r.person2_id, [...(parentsOf.get(r.person2_id) || []), parent])
  }

  // ── pass 1: walk the pedigree, no geometry yet ──
  // breadth-first over Ahnentafel numbers; a cycle in the data (someone recorded
  // as their own ancestor) is stopped by `seen` rather than recursing forever
  type Cell = { ahnen: number; gen: number; person: Person | null; child: Person | null }
  const cells: Cell[] = []
  const queue: Cell[] = [{ ahnen: 1, gen: 0, person: root, child: null }]
  const seen = new Set<string>([root.id])

  while (queue.length) {
    const cell = queue.shift()!
    cells.push(cell)
    const { ahnen, gen, person } = cell
    if (gen >= maxDepth || !person) continue
    const [father, mother] = orderParents(parentsOf.get(person.id) || [])
    for (const [k, parent] of [[0, father], [1, mother]] as const) {
      // a gap is only worth drawing next to a person we know — see the header
      if (parent && seen.has(parent.id)) continue
      if (parent) seen.add(parent.id)
      queue.push({ ahnen: ahnen * 2 + k, gen: gen + 1, person: parent, child: person })
    }
  }

  // ── pass 2: place them ──
  // the canvas is sized from the rings actually drawn, not the rings asked for,
  // so a family recorded two generations deep does not open inside a chart built
  // for six and spend most of its pixels on emptiness
  const depth = Math.max(...cells.map(c => c.gen))
  const outerR = FAN_CORE_R + depth * FAN_RING_W

  // centred on straight up, so the deepest generations arch overhead
  const start = rad(-90 - sweepDeg / 2)
  const sweep = rad(sweepDeg)

  // the viewBox is the arc's real bounding box, not the circle it belongs to: a
  // 270° fan leaves a quadrant open at the bottom, and reserving square space for
  // it would push the whole chart smaller for nothing. Extremes can only occur at
  // the two ends of the arc or where it crosses an axis, plus the centre itself.
  const PAD = 14
  const edges = [start, start + sweep]
  for (let k = -4; k <= 4; k++) {
    const a = (k * Math.PI) / 2
    if (a >= start && a <= start + sweep) edges.push(a)
  }
  const xs = [0, ...edges.map(a => outerR * Math.cos(a))]
  const ys = [0, ...edges.map(a => outerR * Math.sin(a))]
  const minX = Math.min(...xs), maxX = Math.max(...xs)
  const minY = Math.min(...ys), maxY = Math.max(...ys)
  const width = maxX - minX + PAD * 2
  const height = maxY - minY + PAD * 2
  const cx = -minX + PAD
  const cy = -minY + PAD

  const slots: FanSlot[] = []
  let known = 0
  let gaps = 0

  for (const { ahnen, gen, person, child } of cells) {
    const slotsInRing = 2 ** gen
    const i = ahnen - slotsInRing
    const span = sweep / slotsInRing
    const a0 = start + i * span
    const a1 = a0 + span
    const r0 = gen === 0 ? 0 : FAN_CORE_R + (gen - 1) * FAN_RING_W
    const r1 = gen === 0 ? FAN_CORE_R : r0 + FAN_RING_W

    const mid = (a0 + a1) / 2
    const rMid = gen === 0 ? 0 : (r0 + r1) / 2
    // radial text reads outward; on the left half of the chart that would be
    // upside down, so flip it to read inward instead
    const degMid = ((mid * 180) / Math.PI + 360) % 360
    const flip = degMid > 90 && degMid < 270

    if (person) known++; else gaps++

    slots.push({
      ahnen, gen, person,
      childAhnen: gen === 0 ? null : Math.floor(ahnen / 2),
      childPerson: child,
      side: gen === 0 ? 'root' : ahnen % 2 === 0 ? 'father' : 'mother',
      a0, a1, r0, r1,
      path: gen === 0 ? '' : sectorPath(cx, cy, a0, a1, r0, r1),
      labelX: cx + rMid * Math.cos(mid),
      labelY: cy + rMid * Math.sin(mid),
      labelRotate: gen === 0 ? 0 : (degMid + (flip ? 180 : 0)) % 360,
      spanDeg: (span * 180) / Math.PI,
    })
  }

  return { slots, width, height, cx, cy, depth, known, gaps }
}

/**
 * The relationship a slot stands for, read straight out of its Ahnentafel number.
 * Strip the leading 1 from the binary form and each remaining bit is a step: 0 for
 * a father, 1 for a mother. So 5 = 101₂ → father's mother. This is what lets the
 * text fallback name every position without a second traversal of the graph.
 */
export function ahnenTerm(ahnen: number): string {
  if (ahnen <= 1) return 'you'
  const steps = ahnen.toString(2).slice(1).split('').map(b => (b === '0' ? 'father' : 'mother'))
  if (steps.length <= 2) return steps.map((s, i) => (i < steps.length - 1 ? `${s}'s` : s)).join(' ')
  // "great-great-grandfather" beats "father's father's father's father" past two steps
  const last = steps[steps.length - 1]
  const greats = 'great-'.repeat(steps.length - 2)
  return `${greats}grand${last}`
}

/** How much of a name fits in a slot — a wedge 6° wide holds an initial, not a name. */
export function fanNameFits(slot: FanSlot): number {
  const arcPx = (slot.spanDeg / 360) * TAU * ((slot.r0 + slot.r1) / 2)
  return Math.max(0, Math.floor(arcPx / 7.2))
}
